import { AlertTriangle } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { SubsetDenial } from '../types';

import { MonoChip } from './MonoChip';

export interface SubsetDeniedAlertProps {
  denial: SubsetDenial;
  /** Create-surface defaults per design §13.3/§28; attach callers override. */
  title?: string;
  intro?: string;
  resolution?: string;
  className?: string;
}

const DEFAULT_TITLE = "Couldn't create this agent.";
const DEFAULT_INTRO = "It needs permissions you don't have:";
const DEFAULT_RESOLUTION =
  'Ask an administrator to grant them to you, then try again — or go back and pick a different template.';

/**
 * Privilege-ceiling feedback (design §13.3) — the ONE component behind both
 * denial surfaces (create at review, attach in the sequence). Renders the exact
 * denied (action, resource) pairs from the 403 `assigner_lacks_capability`
 * body verbatim — never a bare toast, never only a red border. All draft data
 * survives: only the call failed (E1).
 */
export function SubsetDeniedAlert({
  denial,
  title = DEFAULT_TITLE,
  intro = DEFAULT_INTRO,
  resolution = DEFAULT_RESOLUTION,
  className,
}: SubsetDeniedAlertProps) {
  return (
    <div
      role="alert"
      className={cn(
        'space-y-2 rounded-md border border-destructive/30 border-l-2 border-l-destructive bg-card p-4',
        className,
      )}
    >
      <p className="flex items-center gap-2 text-sm font-medium">
        <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
        {title}
      </p>
      <p className="text-sm">{intro}</p>
      {denial.pairs.length > 0 ? (
        <ul className="space-y-1">
          {denial.pairs.map((pair, index) => (
            <li
              key={`${pair.action}-${pair.resource}-${index}`}
              className="flex flex-wrap gap-1.5 text-sm"
            >
              <MonoChip>{pair.action}</MonoChip>
              <span className="text-muted-foreground">on</span>
              <MonoChip>{pair.resource}</MonoChip>
            </li>
          ))}
        </ul>
      ) : denial.message ? (
        // Pairs missing from the body — the backend message stays verbatim.
        <p className="text-sm text-muted-foreground">{denial.message}</p>
      ) : null}
      <p className="text-sm text-muted-foreground">{resolution}</p>
    </div>
  );
}
